import { useSelector } from 'react-redux';
import { selectRiskSetting } from '@Selectors/Risk.selectors';
import { selectCompletedRun } from '@Selectors/RiskResultsSummary.selectors';

export type ResultsRunState =
	| 'loading'
	| 'complete'
	| 'noCompletedRun'
	| 'notRun';

// Whether Results has something to show for this model:
// - complete: a run has ever succeeded (`hasCompletedRun`, from the backend's
//   lastRunCompleted — survives later failed/cancelled re-runs, unlike
//   `status`), or one completed in this session (recordRunCompleted), which
//   the loaded config doesn't reflect until it's re-fetched.
// - loading: the loaded config is still another model (or none) — never
//   claim "not run" for a model whose config hasn't arrived yet.
// - noCompletedRun: the model has been run (`LastRun` set) but no completed
//   run is recorded — e.g. a success that predates lastRunCompleted followed
//   by a failed re-run, or a first run that failed. Results may exist, so the
//   grid must still render.
// - notRun: this model's config is loaded and it has never been run.
export const useResultsRunState = (configId: string): ResultsRunState => {
	const riskSetting = selectRiskSetting();
	const completedRun = useSelector(selectCompletedRun);
	if (completedRun.configId === configId) return 'complete';
	if (riskSetting.configId !== configId) return 'loading';
	if (riskSetting.hasCompletedRun) return 'complete';
	return riskSetting.LastRun ? 'noCompletedRun' : 'notRun';
};
